import { useMemo } from 'react';
import { useAppSelector } from '../store/hooks';

export interface CurrentUser {
  name: string;
  email: string;
  // Up to two letters for the avatar; falls back to the email so the circle is
  // never empty.
  initials: string;
}

// Keycloak types its token claims as `any`, so every claim is narrowed rather
// than cast.
const asString = (claim: unknown): string => (typeof claim === 'string' ? claim : '');

// Who is signed in, derived from the Keycloak token. Kept out of the header
// components so they only render.
export function useCurrentUser(): CurrentUser {
  const keycloak = useAppSelector((state) => state.authenticationSlice.kcObject);

  return useMemo(() => {
    const parsed = keycloak?.tokenParsed;
    const fullName = asString(parsed?.name);
    const name =
      fullName ||
      [asString(parsed?.given_name), asString(parsed?.family_name)].filter(Boolean).join(' ');
    const email = asString(parsed?.email) || asString(parsed?.preferred_username);

    const fromName = name
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    return { name, email, initials: fromName || email.charAt(0).toUpperCase() };
  }, [keycloak]);
}
